// Task #1664 — Client helpers per cross-modulo bridge.
// NO `any`: tutti i payload tipizzati via `@easyflow/shared`.

import { apiClient } from './api';
import type {
  CrossEntityKind,
  CrossEntityPreview,
  CrossShareDestination,
  CrossShareResult,
  CrossMentionSearchItem,
  FeedMilestoneKind,
  FeedMilestoneSettingRow,
} from '@easyflow/shared';

export async function fetchEntityPreview(
  kind: CrossEntityKind,
  id: string
): Promise<CrossEntityPreview | null> {
  try {
    const res = await apiClient.get<CrossEntityPreview>(
      `/cross/entity?type=${encodeURIComponent(kind)}&id=${encodeURIComponent(id)}`
    );
    return res?.data ?? null;
  } catch {
    return null;
  }
}

export async function shareEntity(
  entity: { kind: CrossEntityKind; id: string },
  destination: CrossShareDestination,
  comment?: string
): Promise<CrossShareResult> {
  const res = await apiClient.post<CrossShareResult>('/cross/share', {
    entity, destination, comment,
  });
  if (!res?.success || !res.data) throw new Error(res?.error || 'Share fallita');
  return res.data;
}

export async function searchMentionCandidates(
  query: string,
  kinds?: CrossEntityKind[],
  limit = 10
): Promise<CrossMentionSearchItem[]> {
  if (!query.trim()) return [];
  const kindsParam = kinds && kinds.length > 0 ? `&kinds=${kinds.join(',')}` : '';
  const res = await apiClient.get<CrossMentionSearchItem[]>(
    `/cross/mention-search?q=${encodeURIComponent(query)}${kindsParam}&limit=${limit}`
  );
  return res?.data ?? [];
}

export async function fetchMilestoneSettings(): Promise<FeedMilestoneSettingRow[]> {
  const res = await apiClient.get<FeedMilestoneSettingRow[]>('/cross/milestone-settings');
  return res?.data ?? [];
}

export async function updateMilestoneSetting(
  kind: FeedMilestoneKind,
  payload: { enabled: boolean; target_type?: 'global' | 'workgroup' | 'department'; target_value?: string | null }
): Promise<void> {
  const res = await apiClient.put(`/cross/milestone-settings/${kind}`, payload);
  if (!res?.success) throw new Error(res?.error || 'Update fallito');
}

// ─── Mention parsing helper (FE) ───────────────────────────────────────
// Pattern canonico: `@<kind>/<uuid>` con kind ∈ lead|deal|contact|task|event|file
export const ENTITY_MENTION_RE = /@(lead|deal|contact|task|event|file)\/([0-9a-f-]{16,36})/gi;

export interface ParsedEntityMention {
  kind: CrossEntityKind;
  id: string;
  start: number;
  end: number;
  raw: string;
}

export function parseEntityMentions(text: string): ParsedEntityMention[] {
  if (!text) return [];
  const out: ParsedEntityMention[] = [];
  const re = new RegExp(ENTITY_MENTION_RE.source, 'gi');
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    const kindStr = m[1];
    const idStr = m[2];
    if (!kindStr || !idStr) continue;
    out.push({
      kind: kindStr.toLowerCase() as CrossEntityKind,
      id: idStr,
      start: m.index,
      end: m.index + m[0].length,
      raw: m[0],
    });
  }
  return out;
}
